import React, { useCallback, useEffect, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { StackScreenProps } from '@react-navigation/stack';
import { BaseAdminScreen, AdminEmptyScreenLayout } from '@/components';
import { OrganizationsScreenSkeleton } from './OrganizationsScreenSkeleton';
import { AdminStackParamList } from '@/navigation/admin/AdminNavigator';
import { useOrganizations } from '@/hooks/admin/useOrganizations';
import { useEntityScreen } from '@/hooks/admin/useEntityScreen';
import {
  OrganizationDataTable,
  DesktopOrganizationSidePanel,
  DesktopCreateOrganizationPanel,
} from '@/components/admin/organizations';
import { IOrganizationRowData, IOrganization } from '@/types/admin/organizations';
import { AdminErrorBoundary } from '@/components/common/admin/mobile/AdminErrorBoundary';
import { useErrorToast } from '@/contexts/admin/ToastContext';
import { DesktopAdminLayout } from '@/components/common/admin/desktop/DesktopAdminLayout';
import { DesktopPageHeader } from '@/components/common/admin/desktop/DesktopPageHeader';
import { DataTableProvider } from '@/contexts/admin/DataTableContext';

type OrganizationsScreenProps = StackScreenProps<AdminStackParamList, 'Organizations'>;

export const OrganizationsScreen: React.FC<OrganizationsScreenProps> = ({
  navigation,
}) => {
  const { showApiError } = useErrorToast();
  const showSearch = false;

  const {
    data,
    tableRows, // Full organization objects
    tableRowsData, // Filtered display data
    isLoading,
    error,
    enhancedError,
    searchQuery,
    updateSearchQuery,
    refresh,
  } = useOrganizations();

  // Use entity screen hook for CSV and layout management
  const {
    csvData,
    layoutMode,
    formatCsvData,
  } = useEntityScreen<IOrganizationRowData>({
    csvHeaders: ['Name', 'Domain', 'Created At'],
    formatCsvRow: (row) => [row.name, row.domain, row.createdAt || 'N/A'],
  });

  // Desktop panel states
  const [showEditPanel, setShowEditPanel] = useState(false);
  const [showCreatePanel, setShowCreatePanel] = useState(false);
  const [selectedOrganization, setSelectedOrganization] = useState<IOrganization | null>(null);

  // Show toast for API errors
  useEffect(() => {
    if (enhancedError) {
      showApiError(enhancedError.originalError, enhancedError.userFriendlyMessage);
    }
  }, [enhancedError, showApiError]);

  // Handle organization selection
  const handleOrganizationSelect = useCallback((organization: IOrganizationRowData) => {
    // Find full organization data (tableRows contains full IOrganization objects)
    const fullOrg = tableRows.find(row => row.id === organization.id);
    if (fullOrg) {
      if (layoutMode === 'desktop') {
        setSelectedOrganization(fullOrg);
        setShowEditPanel(true);
      } else {
        navigation.navigate('EditOrganization', { organization: fullOrg });
      }
    }
  }, [tableRows, layoutMode, navigation]);

  // Handle add organization
  const handleAddOrganization = useCallback(() => {
    if (layoutMode === 'desktop') {
      setSelectedOrganization(null);
      setShowCreatePanel(true);
    } else {
      navigation.navigate('CreateOrganization');
    }
  }, [layoutMode, navigation]);

  // Handle success (refresh and close)
  const handleSuccess = useCallback(() => {
    refresh();
    setShowEditPanel(false);
    setShowCreatePanel(false);
    setSelectedOrganization(null);
  }, [refresh]);

  // Handle close
  const handleClose = useCallback(() => {
    setShowEditPanel(false);
    setShowCreatePanel(false);
    setSelectedOrganization(null);
  }, []);

  // Desktop layout
  if (layoutMode === 'desktop') {
    return (
      <>
        <DesktopAdminLayout>
          <View style={styles.desktopContainer}>
            <DesktopPageHeader
              title="Organizations"
              subtitle=""
              showAddButton={true}
              onAddPress={handleAddOrganization}
              addButtonText="Create Organization"
            />
            
            <AdminErrorBoundary>
              <DataTableProvider 
                tableKey="organizations"
                defaults={{
                  sortModel: [{ field: 'name', sort: 'asc' }],
                }}
              >
                <OrganizationDataTable
                  data={data}
                  isLoading={isLoading}
                  onOrganizationSelect={handleOrganizationSelect}
                  updateCsvData={formatCsvData}
                  showSearch={showSearch}
                />
              </DataTableProvider>
            </AdminErrorBoundary>
          </View>
        </DesktopAdminLayout>

        {/* Desktop Create Panel */}
        <DesktopCreateOrganizationPanel
          visible={showCreatePanel}
          onClose={handleClose}
          onSuccess={handleSuccess}
        />

        {/* Desktop Edit Panel */}
        {selectedOrganization && (
          <DesktopOrganizationSidePanel
            visible={showEditPanel}
            onClose={handleClose}
            organization={selectedOrganization}
            onSuccess={handleSuccess}
          />
        )}
      </>
    );
  }

  // Determine if we have data
  const hasData = data.count > 0;
  
  // Show loading state if we're loading and have no data yet
  const showLoadingState = isLoading && !hasData;

  // Mobile layout
  return (
    <AdminErrorBoundary context="OrganizationsScreen">
      <BaseAdminScreen
        screenName="Organizations"
        navigation={navigation}
        showPlaceholder={false}
        scrollEnabled={false}
        contentContainerStyle={styles.baseScreenContent}
      >
        <AdminEmptyScreenLayout
          title="Organizations"
          emptyStateType="organization"
          onAddPress={handleAddOrganization}
          showAddButton={true}
          buttonType="add"
          hasContent={true}
          disableScrollView={true}
        >
          {showLoadingState ? (
            <OrganizationsScreenSkeleton />
          ) : (
            <DataTableProvider 
              tableKey="organizations"
              defaults={{
                sortModel: [{ field: 'name', sort: 'asc' }],
              }}
            >
              <View style={styles.dataContainer}>
                <OrganizationDataTable
                  data={data}
                  isLoading={isLoading}
                  onOrganizationSelect={handleOrganizationSelect}
                  updateCsvData={formatCsvData}
                  showSearch={showSearch}
                />
              </View>
            </DataTableProvider>
          )}
        </AdminEmptyScreenLayout>
      </BaseAdminScreen>
    </AdminErrorBoundary>
  );
};

const styles = StyleSheet.create({
  baseScreenContent: {
    paddingHorizontal: 0,
    paddingTop: 0,
    paddingBottom: 0,
    flexGrow: 1,
  },
  desktopContainer: {
    flex: 1,
    backgroundColor: 'transparent',
  },
  dataContainer: {
    flex: 1,
    marginHorizontal: -16,
  },
});

